import React, { useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Navbar.css";

const LANGUAGES = [
  { code: "ar", label: "AR" },
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
];

function Navbar() {
  const { t, i18n } = useTranslation();
  const collapseRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const langPrefix = location.pathname === "/fr" || location.pathname.startsWith("/fr/") ? "/fr" : "";

  // French lives at a real /fr/... URL (see App.js + LanguageForcer), while
  // AR/EN share one URL via i18next's language toggle. Switching to French
  // navigates to the /fr-prefixed version of the current page; switching
  // away from French strips the prefix. Either way i18n.changeLanguage keeps
  // every t() call in sync with the URL.
  const switchLanguage = (code) => {
    const pathWithoutPrefix =
      langPrefix === "/fr" ? location.pathname.slice(3) || "/" : location.pathname;

    if (code === "fr" && langPrefix !== "/fr") {
      navigate(`/fr${pathWithoutPrefix === "/" ? "" : pathWithoutPrefix}`);
    } else if (code !== "fr" && langPrefix === "/fr") {
      navigate(pathWithoutPrefix);
      i18n.changeLanguage(code);
    } else {
      i18n.changeLanguage(code);
    }
  };

  // Only close the mobile menu if it's actually open (mobile, expanded).
  // Toggling unconditionally on every click also fires on desktop, where the
  // collapse is forced visible by CSS but the JS state fights that layout.
  const closeMobileMenu = () => {
    const el = collapseRef.current;
    if (el && el.classList.contains('show') && window.bootstrap) {
      window.bootstrap.Collapse.getOrCreateInstance(el).hide();
    }
  };

  return (
    <nav className="navbar navbar-expand-lg navbar-dark bg-navy sticky-top border-bottom border-navy-light shadow-sm">
      <div className="container">
        <Link
          className="navbar-brand fw-semibold letter-spacing-tight"
          to={langPrefix || "/"}
        >
          <div className="d-flex align-items-center gap-2">
            <div className="imgCont">
              <img src="/Images/logo.png" alt={t('footer.officeName')} />
            </div>
            <div>Marwa Hawshar</div>
          </div>
        </Link>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#mainNav"
          aria-controls="mainNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon" />
        </button>
        <div className="collapse navbar-collapse" id="mainNav" ref={collapseRef}>
          <ul className="navbar-nav ms-auto mb-2 mb-lg-0" onClick={closeMobileMenu}>
            <li className="nav-item">
              <Link className="nav-link" to={langPrefix || "/"}>{t('nav.home')}</Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`${langPrefix}/about`}>{t('nav.about')}</Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`${langPrefix}/practice-areas`}>{t('nav.practiceAreas')}</Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`${langPrefix}/practice-areas/lebanese-expat-lawyer-lebanon`}>{t('nav.forExpats')}</Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`${langPrefix}/blog`}>{t('nav.blog')}</Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`${langPrefix}/contact`}>{t('nav.contact')}</Link>
            </li>
          </ul>
          <div className="lang-switch ms-lg-3 mt-2 mt-lg-0 mb-2 mb-lg-0" role="group" aria-label="Switch language">
            {LANGUAGES.map(({ code, label }) => (
              <button
                key={code}
                type="button"
                className={`lang-toggle-btn${i18n.language === code ? ' active' : ''}`}
                onClick={() => switchLanguage(code)}
                aria-pressed={i18n.language === code}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
